import { z } from "zod";
import type { Field } from "./schema";

const id = z
  .string()
  .min(1)
  .max(200)
  .refine((value) => !!value.trim() && !/[\u0000-\u001f\u007f]/.test(value));
const providerId = z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/);
export const damProviderInfoSchema = z.object({
  id: providerId,
  label: z.string().min(1).max(100),
  notice: z.string().min(1).max(1000),
});
export const damBindingSchema = z.object({
  projectId: id,
  fieldId: id,
  partId: id.optional(),
  providerId,
  folderId: id,
  maxBytes: z.number().int().min(1).max(100000000).default(10000000),
});
export const damFolderSchema = z.object({
  id,
  name: z.string().min(1).max(200),
});
export const damItemSchema = z.object({
  id,
  name: z.string().min(1).max(200),
  revision: id,
});
export const damBrowseInputSchema = z.object({
  folderId: id.optional(),
  query: z
    .string()
    .max(500)
    .refine((value) => !/[\u0000-\u001f\u007f]/.test(value))
    .default(""),
  cursor: z.string().min(1).max(1000).optional(),
});
export const damReadInputSchema = z.object({
  folderId: id,
  itemId: id,
  revision: id,
});
export const damPageSchema = z.object({
  path: z.array(damFolderSchema).min(1).max(32),
  folders: z.array(damFolderSchema).max(100),
  items: z.array(damItemSchema).max(100),
  nextCursor: z.string().min(1).max(1000).optional(),
});
export const damReceiptSchema = z
  .object({
    version: z.literal(1),
    providerId,
    rootFolderId: id,
    folderId: id,
    itemId: id,
    revision: id,
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    signature: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export const damSelectionSchema = z.object({
  assetId: z
    .string()
    .regex(/^asset_[a-zA-Z0-9_-]+$/)
    .max(200),
  dam: damReceiptSchema,
});
export type DamProviderInfo = z.infer<typeof damProviderInfoSchema>;
export type DamBinding = z.infer<typeof damBindingSchema>;
export type DamFolder = z.infer<typeof damFolderSchema>;
export type DamItem = z.infer<typeof damItemSchema>;
export type DamBrowseInput = z.infer<typeof damBrowseInputSchema>;
export type DamReadInput = z.infer<typeof damReadInputSchema>;
export type DamPage = z.infer<typeof damPageSchema>;
export type DamSelection = z.infer<typeof damSelectionSchema>;
export interface DamRequestScope {
  tenantId: string;
  subject: string;
  signal: AbortSignal;
}
export interface DamBrowseRequest {
  binding: DamBinding;
  folderId: string;
  query: string;
  cursor?: string;
}
export interface DamReadRequest {
  binding: DamBinding;
  folderId: string;
  itemId: string;
  revision: string;
}
export interface DamFile {
  path: DamFolder[];
  item: DamItem;
  data: Uint8Array;
}
/** The provider authorizes the verified subject's access under binding.folderId. */
export interface DamProvider {
  info: DamProviderInfo;
  browse(input: DamBrowseRequest, scope: DamRequestScope): Promise<DamPage>;
  read(input: DamReadRequest, scope: DamRequestScope): Promise<DamFile>;
}
export interface DamGenerated {
  asset: {
    id: string;
    name: string;
    width: number;
    height: number;
    bytes: number;
  };
  value: DamSelection;
}
export class DamError extends Error {
  constructor(
    readonly code: string,
    readonly status = 422,
  ) {
    super(code);
  }
}

export interface DamFormServices {
  notice?: string;
  browse(
    field: Field,
    input: DamBrowseInput,
    signal: AbortSignal,
  ): Promise<DamPage>;
  select(
    field: Field,
    input: DamReadInput,
    signal: AbortSignal,
  ): Promise<DamGenerated>;
}
/** Call with a stored, effective field after project/part authorization. */
export function damBindingFor(
  projectId: string,
  field: Field,
  partId?: string,
): DamBinding {
  if (
    field.source.kind !== "dam" ||
    !["image", "image-list"].includes(field.type)
  )
    throw new DamError("DAM_FIELD_UNAVAILABLE");
  const parsed = damBindingSchema.safeParse({
    projectId,
    fieldId: field.id,
    partId,
    providerId: field.source.providerId,
    folderId: field.source.folderId,
    maxBytes: field.image.maxBytes,
  });
  if (!parsed.success) throw new DamError("DAM_FIELD_UNAVAILABLE");
  return parsed.data;
}
